import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import {
  AMAZON_OAUTH_MARKETPLACE_KEY,
  AMAZON_OAUTH_REGION_KEY,
  AMAZON_OAUTH_STATE_KEY,
  amazonRedirectUri,
} from '@/features/integrations/amazonOAuth';

/**
 * Amazon Seller Central SP-API OAuth redirect handler. Amazon redirects
 * here with:
 *   - spapi_oauth_code     The authorization code, single-use, ~5-minute TTL.
 *   - state                Echoed back from the consent URL — validated against
 *                          sessionStorage for CSRF protection.
 *   - selling_partner_id   The seller's merchant ID (`A…`-prefixed).
 *   - mws_auth_token       (Optional, legacy MWS migration field — we ignore.)
 *
 * We:
 *   1. Verify `state` against the value stashed by `startAmazonOAuth`. Failing
 *      fast here saves a round-trip; the server re-checks anyway.
 *   2. POST `spapi_oauth_code`, `state`, `selling_partner_id`, the
 *      `marketplace_id` the operator picked, and the redirect_uri to
 *      `amazon-oauth-callback`. The Edge Function exchanges the code for a
 *      refresh token, provisions the sales_channels row, and fires an
 *      initial sync.
 *   3. Show a success toast and navigate back to /integrations.
 */
export function AmazonCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // React 18 StrictMode mounts effects twice in dev; the SP-API
  // authorization code is single-use and Amazon rejects the second
  // exchange (or worse, succeeds and orphans a refresh token).
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('spapi_oauth_code');
    const state = params.get('state');
    const sellingPartnerId = params.get('selling_partner_id');
    // Amazon currently doesn't surface OAuth errors via the redirect (it
    // bounces the seller back to the consent page on failure), but we
    // honour `error` / `error_description` defensively in case that ever
    // changes — Shopify and Google both do.
    const oauthError = params.get('error') ?? params.get('error_description');

    if (oauthError) {
      setError(`Amazon rejected the authorization: ${oauthError}`);
      return;
    }
    if (!code || !state || !sellingPartnerId) {
      setError(
        'Missing OAuth parameters from Amazon. Retry the connection from /integrations.',
      );
      return;
    }

    const expectedState = sessionStorage.getItem(AMAZON_OAUTH_STATE_KEY);
    if (!expectedState || state !== expectedState) {
      setError(
        'OAuth state mismatch. For your security, retry the connection from /integrations.',
      );
      return;
    }
    const marketplaceId = sessionStorage.getItem(AMAZON_OAUTH_MARKETPLACE_KEY);
    if (!marketplaceId) {
      setError(
        'Lost track of which Amazon marketplace you were connecting. Retry the connection from /integrations.',
      );
      return;
    }
    sessionStorage.removeItem(AMAZON_OAUTH_STATE_KEY);
    sessionStorage.removeItem(AMAZON_OAUTH_MARKETPLACE_KEY);
    sessionStorage.removeItem(AMAZON_OAUTH_REGION_KEY);

    (async () => {
      const { data, error: fnError } = await supabase.functions.invoke<{
        ok: boolean;
        channel_name?: string;
        marketplace_id?: string;
        error?: string;
      }>('amazon-oauth-callback', {
        body: {
          code,
          state,
          selling_partner_id: sellingPartnerId,
          marketplace_id: marketplaceId,
          redirect_uri: amazonRedirectUri(),
        },
      });
      if (fnError) {
        const msg = await extractFunctionErrorMessage(fnError);
        setError(msg);
        return;
      }
      if (data && data.ok === false && data.error) {
        setError(data.error);
        return;
      }

      const channelName = data?.channel_name ?? 'Amazon seller';
      toast.success(`Connected ${channelName}. Pulling orders…`);
      navigate('/integrations', { replace: true });
    })();
  }, [params, navigate]);

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <div className="space-y-3 text-center max-w-lg">
          <h1 className="text-xl font-medium tracking-headline">Connection failed</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/integrations', { replace: true })}
            className="text-sm text-primary underline-offset-2 hover:underline"
          >
            Back to Integrations
          </button>
        </div>
      </div>
    );
  }
  return <LoadingScreen />;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as { context?: Response }).context;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}
